import { useEffect } from "react";
import { BrowserRouter, Routes, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import EligibilityModal from "./components/EligibilityModal";
import { HelmetProvider } from "react-helmet-async";
import AuthCallback from "./pages/AuthCallback";
import usePageTracking from "./hooks/usePageTracking";
import { publicRoutes, protectedRoutes, adminRoutes } from "./pages/routeConfig";
import { installSlickInert } from "./utils/a11y/slickInert";
import { loadPropertyClassification } from "./utils/filterDependency";

// A11y: prevent Tab focus on hidden react-slick slides
installSlickInert();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// iter494: Pull canonical property_type → class mapping from backend so the
// listing form's show_if engine stays in sync with admin edits without a
// frontend redeploy. Static seed in filterDependency.js is the fallback.
loadPropertyClassification(API);

// Configure axios defaults
axios.defaults.withCredentials = true;

// iter397: CSRF double-submit cookie interceptor.
// The backend sets a readable `csrf_token` cookie on login / first authenticated
// response. For every state-changing request we echo it back via X-CSRF-Token.
// This pairs with utils/csrf.py on the backend — mismatches return 403.
const CSRF_SAFE_METHODS = new Set(["get", "head", "options"]);
function _readCookie(name) {
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}
axios.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!CSRF_SAFE_METHODS.has(method)) {
    const token = _readCookie("csrf_token");
    if (token) {
      config.headers = config.headers || {};
      // Only set if caller didn't already specify one (e.g. during tests)
      if (!config.headers["X-CSRF-Token"]) {
        config.headers["X-CSRF-Token"] = token;
      }
    }
  }
  return config;
});

// Add response interceptor for auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // iter397: attach X-Request-ID to every error object so support / toast
    // components can surface it. Backend sets it on every response.
    const rid = error.response?.headers?.["x-request-id"] || error.response?.headers?.["X-Request-ID"];
    if (rid) {
      error.requestId = rid;
      // Keep a shortened, user-friendly version for toast display
      error.requestIdShort = String(rid).slice(0, 8);
    }
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      // These endpoints are anonymous-tolerant: a 401 here is expected for guests
      // and must NOT trigger a forced redirect to /login (Iter456: keep listing detail public).
      const isAuthCheck =
        requestUrl.includes('/auth/me') ||
        requestUrl.includes('/init-data') ||
        requestUrl.includes('/favorites/') ||
        requestUrl.includes('/recently-viewed') ||
        requestUrl.includes('/job-applications/check') ||
        requestUrl.includes('/price-alerts') ||
        requestUrl.includes('/users/listing-quota') ||
        requestUrl.includes('/user/listing-quota') ||
        requestUrl.includes('/cart/');
      if (!isAuthCheck && currentPath !== '/login' && currentPath !== '/register') {
        window.location.href = '/login?expired=1';
      }
    }
    // Iter423: super admin without 2FA — bounce to forced setup screen.
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;
      if (code === '2fa_setup_required' && window.location.pathname !== '/setup-2fa') {
        window.location.href = '/setup-2fa';
      }
    }
    return Promise.reject(error);
  }
);

function AppRouter() {
  const location = useLocation();

  usePageTracking();

  useEffect(() => {
    if (location.pathname.includes('abonelik')) return;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, location.search]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {publicRoutes}
      {protectedRoutes}
      {adminRoutes}
    </Routes>
  );
}

import { ToastSettingsProvider, useToastSettings } from './contexts/ToastSettingsContext';

const DynamicToaster = () => {
  const settings = useToastSettings();
  return (
    <Toaster 
      position={settings.position} 
      richColors 
      closeButton={settings.showCloseButton}
      duration={settings.duration}
      visibleToasts={settings.stackLimit}
    />
  );
};

import { SiteSettingsProvider } from "./contexts/SiteSettingsContext";
import { ProvinceProvider } from "./contexts/ProvinceContext";

function App() {
  return (
    <HelmetProvider>
    <div className="App min-h-screen bg-background">
      <SiteSettingsProvider>
        <ProvinceProvider>
          <ToastSettingsProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <DynamicToaster />
            <EligibilityModal />
          </ToastSettingsProvider>
        </ProvinceProvider>
      </SiteSettingsProvider>
    </div>
    </HelmetProvider>
  );
}

export default App;
